/**
 * BaseApiClient - Shared HTTP client for all services
 * Provides consistent interface with auth, retry logic, and error handling
 */

import axios from 'axios';

export class BaseApiClient {
  constructor(config) {
    this.config = config;
    this.baseURL = config.baseURL;
    this.serviceName = config.serviceName || 'unknown';
    this.authProvider = config.authProvider;
    this.retryConfig = {
      attempts: 3,
      delay: 1000,
      backoff: 2,
      ...config.retry
    };

    // Create axios instance
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: config.timeout || 30000,
      headers: {
        'Content-Type': 'application/json',
        ...config.headers
      }
    });

    this._setupInterceptors();
  }

  /**
   * Setup request and response interceptors
   */
  _setupInterceptors() {
    // Request interceptor - add auth and logging
    this.client.interceptors.request.use(
      (config) => {
        // Add authentication token
        if (this.authProvider) {
          const token = this.authProvider.getToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        // Handle FormData requests
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }

        // Enhanced logging
        console.group(`🚀 [${this.serviceName}] ${config.method.toUpperCase()} ${config.url}`);
        console.log('Base URL:', this.baseURL);
        console.log('Headers:', config.headers);

        if (config.data && !(config.data instanceof FormData)) {
          console.log('Data:', config.data);
        }
        console.groupEnd();

        return config;
      },
      (error) => {
        console.error(`❌ [${this.serviceName}] Request Error:`, error);
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle errors and logging
    this.client.interceptors.response.use(
      (response) => {
        console.group(`✅ [${this.serviceName}] Response (${response.status})`);
        console.log('Data:', response.data);
        console.groupEnd();
        return response;
      },
      (error) => {
        console.group(`❌ [${this.serviceName}] Response Error`);
        console.error('Status:', error.response?.status);
        console.error('Data:', error.response?.data);
        console.error('Message:', error.message);
        console.groupEnd();

        return Promise.reject(this._enhanceError(error));
      }
    );
  }

  /**
   * Enhance error with service context
   */
  _enhanceError(error) {
    const enhanced = new Error();
    enhanced.serviceName = this.serviceName;
    enhanced.originalError = error;
    enhanced.message = error.message;
    enhanced.status = error.response?.status;
    enhanced.data = error.response?.data;

    // Add service-specific error context
    if (error.response?.status === 401) {
      enhanced.message = `Authentication failed for ${this.serviceName} service`;
      enhanced.type = 'AUTH_ERROR';
    } else if (error.response?.status >= 500) {
      enhanced.message = `${this.serviceName} service is temporarily unavailable`;
      enhanced.type = 'SERVICE_ERROR';
    } else if (error.code === 'ECONNABORTED') {
      enhanced.message = `${this.serviceName} service request timed out`;
      enhanced.type = 'TIMEOUT_ERROR';
    } else {
      enhanced.type = 'REQUEST_ERROR';
    }

    return enhanced;
  }

  /**
   * Make HTTP request with retry logic
   */
  async request(method, endpoint, data = null, options = {}) {
    const config = {
      method,
      url: endpoint,
      ...options
    };

    if (data) {
      if (method.toLowerCase() === 'get') {
        config.params = data;
      } else {
        config.data = data;
      }
    }

    return this._executeWithRetry(() => this.client.request(config));
  }

  /**
   * Execute request with retry logic
   */
  async _executeWithRetry(requestFn, attempt = 1) {
    try {
      return await requestFn();
    } catch (error) {
      // Don't retry client errors (4xx) or if we've exceeded max attempts
      if (
        attempt >= this.retryConfig.attempts ||
        (error.response?.status >= 400 && error.response?.status < 500)
      ) {
        throw error;
      }

      // Calculate delay with exponential backoff
      const delay = this.retryConfig.delay * Math.pow(this.retryConfig.backoff, attempt - 1);

      console.warn(`⚠️ [${this.serviceName}] Retry attempt ${attempt + 1}/${this.retryConfig.attempts} in ${delay}ms`);

      await new Promise(resolve => setTimeout(resolve, delay));
      return this._executeWithRetry(requestFn, attempt + 1);
    }
  }

  // HTTP method shortcuts
  async get(endpoint, params = null, options = {}) {
    return this.request('GET', endpoint, params, options);
  }

  async post(endpoint, data = null, options = {}) {
    return this.request('POST', endpoint, data, options);
  }

  async put(endpoint, data = null, options = {}) {
    return this.request('PUT', endpoint, data, options);
  }

  async patch(endpoint, data = null, options = {}) {
    return this.request('PATCH', endpoint, data, options);
  }

  async delete(endpoint, options = {}) {
    return this.request('DELETE', endpoint, null, options);
  }

  /**
   * Update auth provider (for when user logs in/out)
   */
  setAuthProvider(authProvider) {
    this.authProvider = authProvider;
  }

  /**
   * Get service health/status
   */
  async healthCheck() {
    try {
      const response = await this.get('/health');
      return {
        service: this.serviceName,
        status: 'healthy',
        response: response.data
      };
    } catch (error) {
      return {
        service: this.serviceName,
        status: 'unhealthy',
        error: error.message
      };
    }
  }
}

export default BaseApiClient;